import type {
  AdminAuditLogListResponse,
  AdminAuditTimelineResponse,
  AdminPermissionListResponse,
  AdminRoleListResponse,
  AdminSessionRevocationResponse,
  AdminSessionRevocationResult,
  AdminUserListResponse,
  AdminUserResponse,
  AdminUserStatus,
} from '@mds/shared/types/admin';

import { api } from '@/lib/api';

/**
 * The Phase 1 administration endpoints — see
 * `backend/docs/admin-administration-api.md`.
 *
 * List endpoints return the shared `{ data, pagination }` envelope and are
 * handed back whole, because the tables need the pagination block. Single-item
 * endpoints are unwrapped to the payload.
 */

// A type alias rather than an interface: only aliases carry the implicit index
// signature the client's `query` parameter needs.
export type AdminUserQuery = {
  page?: number;
  limit?: number;
  q?: string;
  status?: AdminUserStatus;
  roleId?: string;
  sort?: 'createdAt' | 'updatedAt' | 'lastLoginAt' | 'email' | 'firstName' | 'lastName' | 'status';
  direction?: 'asc' | 'desc';
};

export const listUsers = (query: AdminUserQuery = {}) =>
  api.get<AdminUserListResponse>('admin/users', { query });

export const getUser = (userId: string) =>
  api.get<AdminUserResponse>(`admin/users/${userId}`).then((r) => r.data);

export interface CreateAdminUserBody {
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: AdminUserStatus;
  password: string;
  roleIds: string[];
}

export const createUser = (body: CreateAdminUserBody) =>
  api.post<AdminUserResponse>('admin/users', body).then((r) => r.data);

/** Any subset. `null` clears a name or phone; email and status have side effects. */
export type UpdateAdminUserBody = Partial<{
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: AdminUserStatus;
}>;

export const updateUser = (userId: string, body: UpdateAdminUserBody) =>
  api.patch<AdminUserResponse>(`admin/users/${userId}`, body).then((r) => r.data);

/** Replaces the whole set — must contain at least one active role. */
export const replaceRoles = (userId: string, roleIds: string[]) =>
  api.put<AdminUserResponse>(`admin/users/${userId}/roles`, { roleIds }).then((r) => r.data);

/** Idempotent. Returns how many sessions were actually ended. */
export const revokeSessions = (userId: string): Promise<AdminSessionRevocationResult> =>
  api
    .post<AdminSessionRevocationResponse>(`admin/users/${userId}/revoke-sessions`)
    .then((r) => r.data);

export const listRoles = (query: { q?: string; includeInactive?: boolean } = {}) =>
  api.get<AdminRoleListResponse>('admin/roles', { query }).then((r) => r.data);

export const listPermissions = (query: { q?: string } = {}) =>
  api.get<AdminPermissionListResponse>('admin/permissions', { query }).then((r) => r.data);

export type AuditQuery = {
  page?: number;
  limit?: number;
  actorType?: 'admin' | 'customer' | 'system';
  actorId?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  /** ISO 8601 with an offset. `from` cannot follow `to`. */
  from?: string;
  to?: string;
  q?: string;
};

export const listAuditLogs = (query: AuditQuery = {}) =>
  api.get<AdminAuditLogListResponse>('admin/audit-logs', { query });

/** The per-record timeline, newest first. */
export const auditTimeline = (entityType: string, entityId: string, limit?: number) =>
  api
    .get<AdminAuditTimelineResponse>(`admin/audit-logs/${entityType}/${entityId}`, {
      query: { limit },
    })
    .then((r) => r.data);
